// MatchReasonCard — visual breakdown of a Reason Object.
//
// The contract (returned by `services/match_explainer.py::to_reason`):
//   {
//     score, confidence (high/medium/low/none),
//     rules_fired:  [{rule, label, points, max, ratio, evidence}],
//     rules_excluded: [{rule, label, reason}],
//     breakdown, rule_weights, candidate,
//   }
//
// Pure renderer. Zero data fetching, zero logic — the backend
// computed the entire narrative.
import React from 'react';
import { CheckCircle2, XCircle, Sparkles, AlertCircle } from 'lucide-react';

const CONFIDENCE_META = {
  high:   { bg: 'bg-emerald-100', fg: 'text-emerald-800', label: 'ثقة عالية' },
  medium: { bg: 'bg-amber-100',   fg: 'text-amber-800',   label: 'ثقة متوسطة' },
  low:    { bg: 'bg-rose-100',    fg: 'text-rose-800',    label: 'ثقة منخفضة' },
  none:   { bg: 'bg-slate-100',   fg: 'text-slate-600',   label: 'بلا ثقة' },
};

function RulePill({ rule, fired }) {
  const tone = fired
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-rose-50 text-rose-700 border-rose-200';
  const Icon = fired ? CheckCircle2 : XCircle;
  return (
    <div
      data-testid={`reason-${fired ? 'fired' : 'excluded'}-${rule.rule}`}
      className={`border rounded-lg px-3 py-2 ${tone}`}
    >
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="text-xs font-semibold">{rule.label}</span>
        {fired && (
          <span className="ms-auto font-mono text-[10px] opacity-70">
            {rule.points}/{rule.max}
          </span>
        )}
      </div>
      <div className="text-[11px] opacity-80 mt-1 pe-5 leading-snug truncate" title={rule.evidence || rule.reason}>
        {rule.evidence || rule.reason}
      </div>
    </div>
  );
}

export default function MatchReasonCard({ reason, compact = false }) {
  if (!reason) {
    return (
      <div
        data-testid="reason-empty"
        className="flex items-center gap-2 text-xs text-slate-400 italic py-2"
      >
        <AlertCircle className="w-3.5 h-3.5" />
        لم يتم حساب تفسير للمطابقة بعد
      </div>
    );
  }
  const conf = CONFIDENCE_META[reason.confidence] || CONFIDENCE_META.none;
  const fired = reason.rules_fired || [];
  const excluded = reason.rules_excluded || [];

  return (
    <div
      data-testid="match-reason-card"
      className="bg-white border border-slate-200 rounded-xl p-3 space-y-3"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-indigo-500" />
        <span className="text-sm font-semibold text-slate-800">تفسير المطابقة</span>
        <span
          data-testid="reason-confidence"
          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${conf.bg} ${conf.fg}`}
        >
          {conf.label}
        </span>
        <span data-testid="reason-score" className="ms-auto font-mono text-sm font-bold text-slate-700">
          {reason.score}/100
        </span>
      </div>

      {!compact && reason.candidate && (
        <div className="bg-slate-50 rounded-lg p-2 text-[11px] text-slate-600 grid grid-cols-2 gap-x-3 gap-y-0.5">
          <div><span className="opacity-60">المرشّح:</span> {reason.candidate.label || '—'}</div>
          <div><span className="opacity-60">النوع:</span> {reason.candidate.kind || '—'}</div>
          <div><span className="opacity-60">المبلغ:</span> {reason.candidate.amount ?? '—'}</div>
          <div><span className="opacity-60">التاريخ:</span> {reason.candidate.date || '—'}</div>
        </div>
      )}

      {fired.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
            معايير تحققت ({fired.length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {fired.map((r) => (
              <RulePill key={r.rule} rule={r} fired />
            ))}
          </div>
        </div>
      )}

      {excluded.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
            معايير لم تتحقق ({excluded.length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {excluded.map((r) => (
              <RulePill key={r.rule} rule={r} fired={false} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
